/**
 * "Customise this plan" — a tested aged care plan, in Manual Control's own terms.
 *
 * Before 25 Oct 2026 only the deposit funding carried over, so a plan that also cleared a debt opened
 * with the debt card at $0 and the hand-built figures disagreed with the card the client had just read
 * (short $43,248 against $37,548 on a $15,000 credit card). The debt payoff, its funding and its
 * repayment basis now come across in the same key spaces the engine used (`debtPayoffKey` for the
 * debts, `type-id` for the assets), and a plan that lets the home brings its rent with it.
 *
 * EVERY PART OF THE PLAN (owner, 25 Oct 2026: "Customise this plan" copies the suggested plan and lets
 * them make edits). The home sold or downsized (its sale, the new home, the downsizer contribution),
 * an investment property sold, a reverse mortgage or the Home Equity Access Scheme, the guaranteed
 * income for life, funeral bonds or a gift, and the deposit's drawdown arrangement now come across
 * too, each into the field Manual Control itself keeps it in, so the hand-built figures open equal to
 * the card's. Where the engine funded a lever from two accounts, Manual Control's single "paid from"
 * takes the one that paid most (see `sourceKey`).
 */
import { flattenSeRadFundingToManualKeys } from '../../utils/suggestionEngineFunding.ts';

type Bag = Record<string, number>;
type Opts = Record<string, unknown>;

export interface AcBuildOnSeed {
    radFunding: Bag;
    debtPayments: Bag;
    debtFunding: Bag;
    debtRepaymentBasis: 'keep_repayment' | 'lower_repayment';
    /** Null where the row says nothing about the home, so the caller leaves the home as it is. */
    home: { rentHome: boolean; rentAmount?: number; rentExpenses?: number } | null;
    /**
     * The levers beyond the deposit and the debts, in Manual Control's shapes. Null where the row is
     * not an engine plan (a blank start), so the caller resets them instead.
     */
    levers: AcLeverSeed | null;
}

export interface AcLeverSeed {
    overallStrategy: { rentHome: boolean; downsizeHome: boolean; accessHomeEquity: boolean; reverseMortgage: boolean; heas: boolean; sellInvestmentProperty: boolean };
    strategyInputs: { rentAmount?: number; rentExpenses?: number };
    /** Merged over Manual Control's own defaults by the caller; only what the row sets. */
    downsizeDetails: Opts | null;
    investmentPropertyDetails: Opts | null;
    reverseMortgageDetails: Opts | null;
    heasDetails: Opts | null;
    otherStrategies: { gifting: boolean; giftingAmount: number; giftingSource: string; funeralBonds: boolean; funeralBondAmount: number; funeralBondSource: string; annuity: boolean };
    /** The guaranteed income, as Manual Control's annuity record holds it; null when the plan buys none. */
    annuityRecord: Opts | null;
    radDrawdown: 'fees' | 'dap' | 'none' | null;
    /** The living costs the plan was priced on (it adds the partner's rent where the home is let). */
    scenarioLivingExpenses: number | null;
}

const positive = (bag: Record<string, unknown> | undefined): Bag =>
    Object.fromEntries(Object.entries(bag || {}).map(([k, v]) => [k, Number(v) || 0]).filter(([, v]) => (v as number) > 0)) as Bag;

const num = (v: unknown): number | undefined => (v == null || v === '' || !Number.isFinite(Number(v)) ? undefined : Number(v));

/** Manual Control's single "paid from" for a lever the engine funded from a list of accounts: the largest. */
function sourceKey(sources: unknown): { key: string; type: string; id: string } | null {
    const list = Array.isArray(sources) ? (sources as Array<{ type?: unknown; id?: unknown; amountToTake?: unknown }>) : [];
    const top = [...list].filter((s) => s && s.type && s.id != null).sort((a, b) => (Number(b.amountToTake) || 0) - (Number(a.amountToTake) || 0))[0];
    return top ? { key: `${String(top.type)}-${String(top.id)}`, type: String(top.type), id: String(top.id) } : null;
}

interface PeopleContext {
    clientAge?: number | null;
    partnerAge?: number | null;
    isCouple?: boolean;
    /** Home value, for the sale price a downsize row leaves to the home's value. */
    homeValue?: number;
}

function leversOf(opts: Opts, meansTest: Record<string, unknown> | null, ctx: PeopleContext): AcLeverSeed {
    const downsize = opts.downsizeHome === true;
    const sellIp = opts.sellInvestmentProperty === true && !!opts.investmentPropertyDetails;
    const rm = opts.reverseMortgage === true && !!opts.reverseMortgageDetails;
    const heas = opts.heas === true && !!opts.heasDetails;
    const homeValue = Number(ctx.homeValue) || 0;
    const sellingCosts = num(opts.sellingCosts);
    const type = meansTest?.type;
    const src = sourceKey(meansTest?.fundingSources);
    const amountOf = (k: string) => Math.max(0, Number(meansTest?.[k]) || 0);

    let annuityRecord: Opts | null = null;
    if (type === 'annuity' && amountOf('purchaseAmount') > 0) {
        const owner = meansTest?.ownership === 'partner' ? 'partner' : meansTest?.ownership === 'joint' ? 'joint' : 'client';
        const guaranteed = meansTest?.annuityType === 'guaranteed';
        annuityRecord = {
            id: 'aged-care-annuity',
            annuityType: guaranteed ? 'guaranteed' : 'lifetime',
            purchaseAmount: amountOf('purchaseAmount'),
            residualCapital: guaranteed ? 1 : Math.max(0, Math.min(1, Number(meansTest?.residualCapital) || 0)),
            owner,
            fundingSource: src
                ? { id: src.key, type: src.type, assetId: src.id, label: src.type, ownership: owner === 'joint' ? 'joint' : owner }
                : { id: 'bank-default', type: 'bank', assetId: null, label: 'Bank accounts / cash', ownership: ctx.isCouple ? 'joint' : 'client' },
            indexation: meansTest?.indexation === true,
            reversionary: false,
            // Priced on the person who owns it, as the engine prices it.
            age: owner === 'partner' ? (ctx.partnerAge ?? null) : (ctx.clientAge ?? null),
            partnerAge: ctx.isCouple ? (owner === 'partner' ? (ctx.clientAge ?? null) : (ctx.partnerAge ?? null)) : null,
            ...(num(meansTest?.incomeOverride) ? { incomeOverride: num(meansTest?.incomeOverride) } : {}),
        };
    }

    const drawdown = opts.radDrawdown;
    return {
        overallStrategy: {
            rentHome: opts.rentHome === true,
            downsizeHome: downsize,
            accessHomeEquity: rm || heas || opts.accessHomeEquity === true,
            reverseMortgage: rm,
            heas,
            sellInvestmentProperty: sellIp,
        },
        strategyInputs: {
            ...(opts.rentHome === true && num(opts.rentAmount) != null ? { rentAmount: num(opts.rentAmount) } : {}),
            ...(opts.rentHome === true && num(opts.rentExpenses) != null ? { rentExpenses: num(opts.rentExpenses) } : {}),
        },
        downsizeDetails: downsize
            ? {
                  // The engine sells the home at its value and carries the selling costs beside the details.
                  salePrice: homeValue,
                  ...(sellingCosts != null ? { sellingCosts } : {}),
                  ...((opts.downsizeDetails as Opts) || {}),
              }
            : null,
        investmentPropertyDetails: sellIp ? { ...(opts.investmentPropertyDetails as Opts) } : null,
        reverseMortgageDetails: rm ? { ...(opts.reverseMortgageDetails as Opts) } : null,
        heasDetails: heas ? { ...(opts.heasDetails as Opts) } : null,
        otherStrategies: {
            gifting: type === 'gifting' && amountOf('amount') > 0,
            giftingAmount: type === 'gifting' ? amountOf('amount') : 0,
            giftingSource: type === 'gifting' && src ? src.key : 'bankAccounts',
            funeralBonds: type === 'funeralBonds' && amountOf('amount') > 0,
            funeralBondAmount: type === 'funeralBonds' ? amountOf('amount') : 0,
            funeralBondSource: type === 'funeralBonds' && src ? src.key : 'bankAccounts',
            annuity: !!annuityRecord,
        },
        annuityRecord,
        radDrawdown: drawdown === 'fees' || drawdown === 'dap' || drawdown === 'none' ? drawdown : null,
        scenarioLivingExpenses: num(opts.scenarioLivingExpenses) ?? null,
    };
}

export function acBuildOnSeed(row: unknown, ctx: PeopleContext = {}): AcBuildOnSeed {
    const r = (row || {}) as { radFunding?: unknown; strategyOptions?: Opts; meansTestStrategy?: Record<string, unknown> | null };
    const opts = r.strategyOptions || {};
    const payoff = (opts.debtPayoff || null) as { payments?: Record<string, unknown>; funding?: Record<string, unknown>; repaymentBasis?: string } | null;
    const keepsOrLets = opts.downsizeHome !== true && !opts.sellInvestmentProperty && !opts.reverseMortgage && !opts.heas;
    return {
        radFunding: r.radFunding ? (flattenSeRadFundingToManualKeys(r.radFunding as never) as Bag) : {},
        debtPayments: positive(payoff?.payments),
        debtFunding: positive(payoff?.funding),
        debtRepaymentBasis: payoff?.repaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment',
        home: !row || !keepsOrLets || typeof opts.rentHome !== 'boolean'
            ? null
            : opts.rentHome
                ? { rentHome: true, ...(Number(opts.rentAmount) > 0 ? { rentAmount: Number(opts.rentAmount) } : {}), ...(Number(opts.rentExpenses) >= 0 && opts.rentExpenses != null ? { rentExpenses: Number(opts.rentExpenses) } : {}) }
                : { rentHome: false },
        levers: row ? leversOf(opts, r.meansTestStrategy || null, ctx) : null,
    };
}
